/**
 * Iter53 P47 — Contract & Loc consum tab extracted from SaleForm.js.
 *
 * Pure presentation component. Uses ONLY the SaleFormContext (formData, handleChange,
 * canWrite, isHidden, isEdit). No external state, no side effects.
 *
 * Sections rendered:
 *   - Warning banner (create mode: contract details are set after save)
 *   - Card 1 — Date contract (numbers, types, product names, duration, bundle flag)
 *   - Card 2 — Comercial & lifecycle (prices, distributor, key dates)
 *   - Card 3 — Loc consum (NLC/PDR/CLC + address)
 */
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { useSaleFormContext } from "./SaleFormContext";

export const SaleFormContractTab = () => {
  const { formData, handleChange, canWrite, isHidden, isEdit } = useSaleFormContext();

  return (
    <>
      {!isEdit && (
        <div
          className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="contract-tab-create-warning"
        >
          Detaliile contractului se vor configura după salvarea vânzării (folosește pagina de Editare).
        </div>
      )}

      {/* Date contract */}
      <Card data-testid="contract-tab-section-identity">
        <CardHeader>
          <CardTitle className="text-base">Date contract</CardTitle>
          <CardDescription>Numere, tipuri și denumiri ofertă. Modificările se salvează la &bdquo;Actualizează&rdquo;.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!isHidden("contract.contract_number") && (
            <div className="space-y-2">
              <Label>
                Nr. contract <span className="text-xs text-slate-400 font-normal">(real, ex: VI903483)</span>
              </Label>
              <Input value={formData.contract_number}
                onChange={(e) => handleChange("contract_number", e.target.value)}
                placeholder="VI903483"
                disabled={!isEdit || !canWrite("contract.contract_number")}
                data-testid="contract_number" />
            </div>
          )}
          {!isHidden("contract.contract_number_old") && (
            <div className="space-y-2">
              <Label>Nr. contract vechi (legacy)</Label>
              <Input value={formData.contract_number_old}
                onChange={(e) => handleChange("contract_number_old", e.target.value)}
                disabled={!isEdit || !canWrite("contract.contract_number_old")}
                data-testid="contract_number_old" />
            </div>
          )}
          {!isHidden("contract.contract_type") && (
            <div className="space-y-2">
              <Label>Tip contract</Label>
              <Input value={formData.contract_type}
                onChange={(e) => handleChange("contract_type", e.target.value)}
                placeholder="nou / transfer / reînnoire"
                disabled={!isEdit || !canWrite("contract.contract_type")}
                data-testid="contract_type" />
            </div>
          )}
          {!isHidden("contract.customer_category") && (
            <div className="space-y-2">
              <Label>Tip client</Label>
              <Select value={formData.customer_category}
                onValueChange={(v) => handleChange("customer_category", v)}
                disabled={!isEdit || !canWrite("contract.customer_category")}>
                <SelectTrigger data-testid="customer_category"><SelectValue placeholder="Selectează..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Casnic">Casnic (Rezidențial)</SelectItem>
                  <SelectItem value="Non-casnic">Non-casnic (Business)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {!isHidden("contract.offer_type") && (
            <div className="space-y-2">
              <Label>Categorie ofertă</Label>
              <Select value={formData.offer_type}
                onValueChange={(v) => handleChange("offer_type", v)}
                disabled={!isEdit || !canWrite("contract.offer_type")}>
                <SelectTrigger data-testid="offer_type"><SelectValue placeholder="Selectează..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Rezidential">Rezidențial</SelectItem>
                  <SelectItem value="Business">Business</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {!isHidden("contract.raw_product_name") && (
            <div className="space-y-2 md:col-span-2">
              <Label>Denumire ofertă (raw, din import)</Label>
              <Input value={formData.raw_product_name}
                onChange={(e) => handleChange("raw_product_name", e.target.value)}
                disabled={!isEdit || !canWrite("contract.raw_product_name")}
                data-testid="raw_product_name" />
            </div>
          )}
          {!isHidden("contract.commercial_product_name") && (
            <div className="space-y-2 md:col-span-2">
              <Label>Produs comercial</Label>
              <Input value={formData.commercial_product_name}
                onChange={(e) => handleChange("commercial_product_name", e.target.value)}
                disabled={!isEdit || !canWrite("contract.commercial_product_name")}
                data-testid="commercial_product_name" />
            </div>
          )}
          {!isHidden("contract.is_bundle") && (
            <div className="space-y-2 flex items-center gap-2 pt-6">
              <Checkbox id="is_bundle" checked={formData.is_bundle}
                onCheckedChange={(checked) => handleChange("is_bundle", !!checked)}
                disabled={!isEdit || !canWrite("contract.is_bundle")}
                data-testid="is_bundle" />
              <Label htmlFor="is_bundle" className="cursor-pointer">Ofertă Bundle (electric + gaz / VAS)</Label>
            </div>
          )}
          {!isHidden("contract.contract_duration_months") && (
            <div className="space-y-2">
              <Label>Durată contract (luni)</Label>
              <Input type="number" value={formData.contract_duration_months}
                onChange={(e) => handleChange("contract_duration_months", e.target.value)}
                placeholder="ex: 12"
                disabled={!isEdit || !canWrite("contract.contract_duration_months")}
                data-testid="contract_duration_months" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Comercial + Dates */}
      <Card data-testid="contract-tab-section-commercial">
        <CardHeader>
          <CardTitle className="text-base">Comercial &amp; lifecycle</CardTitle>
          <CardDescription>Prețuri, distribuitor, date cheie ale ciclului de viață al contractului.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!isHidden("contract.price_kwh") && (
            <div className="space-y-2">
              <Label>Preț kWh (Electric, lei)</Label>
              <Input type="number" step="0.0001" value={formData.price_kwh}
                onChange={(e) => handleChange("price_kwh", e.target.value)}
                disabled={!isEdit || !canWrite("contract.price_kwh")}
                data-testid="price_kwh" />
            </div>
          )}
          {!isHidden("contract.price_kwh_gas") && (
            <div className="space-y-2">
              <Label>Preț kWh (Gaz, lei)</Label>
              <Input type="number" step="0.0001" value={formData.price_kwh_gas}
                onChange={(e) => handleChange("price_kwh_gas", e.target.value)}
                disabled={!isEdit || !canWrite("contract.price_kwh_gas")}
                data-testid="price_kwh_gas" />
            </div>
          )}
          {!isHidden("contract.distributor_name") && (
            <div className="space-y-2">
              <Label>Distribuitor</Label>
              <Input value={formData.distributor_name}
                onChange={(e) => handleChange("distributor_name", e.target.value)}
                disabled={!isEdit || !canWrite("contract.distributor_name")}
                data-testid="distributor_name" />
            </div>
          )}
          {!isHidden("contract.contract_date") && (
            <div className="space-y-2">
              <Label>Data contract (dd.mm.yyyy)</Label>
              <Input value={formData.contract_date}
                onChange={(e) => handleChange("contract_date", e.target.value)}
                placeholder="dd.mm.yyyy"
                disabled={!isEdit || !canWrite("contract.contract_date")}
                data-testid="contract_date" />
            </div>
          )}
          {!isHidden("contract.start_date") && (
            <div className="space-y-2">
              <Label>Data start contract</Label>
              <Input value={formData.start_date}
                onChange={(e) => handleChange("start_date", e.target.value)}
                placeholder="dd.mm.yyyy"
                disabled={!isEdit || !canWrite("contract.start_date")}
                data-testid="start_date" />
            </div>
          )}
          {!isHidden("contract.activation_date") && (
            <div className="space-y-2">
              <Label>Data activare</Label>
              <Input value={formData.activation_date}
                onChange={(e) => handleChange("activation_date", e.target.value)}
                placeholder="dd.mm.yyyy"
                disabled={!isEdit || !canWrite("contract.activation_date")}
                data-testid="activation_date" />
            </div>
          )}
          {!isHidden("contract.cancellation_date") && (
            <div className="space-y-2">
              <Label>Data anulare</Label>
              <Input value={formData.cancellation_date}
                onChange={(e) => handleChange("cancellation_date", e.target.value)}
                placeholder="dd.mm.yyyy"
                disabled={!isEdit || !canWrite("contract.cancellation_date")}
                data-testid="cancellation_date" />
            </div>
          )}
          {!isHidden("contract.csd_date") && (
            <div className="space-y-2">
              <Label>Data CSD</Label>
              <Input value={formData.csd_date}
                onChange={(e) => handleChange("csd_date", e.target.value)}
                placeholder="dd.mm.yyyy"
                disabled={!isEdit || !canWrite("contract.csd_date")}
                data-testid="csd_date" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Loc consum */}
      <Card data-testid="contract-tab-section-loc-consum">
        <CardHeader>
          <CardTitle className="text-base">Loc consum</CardTitle>
          <CardDescription>Identificatori și adresă punct de consum. POD/NLC se setează din tab-ul &bdquo;Detalii vânzare&rdquo;.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {!isHidden("contract.nlc_gaz") && (
            <div className="space-y-2">
              <Label>NLC gaz</Label>
              <Input value={formData.nlc_gaz}
                onChange={(e) => handleChange("nlc_gaz", e.target.value)}
                disabled={!isEdit || !canWrite("contract.nlc_gaz")}
                data-testid="nlc_gaz" />
            </div>
          )}
          {!isHidden("contract.pdr") && (
            <div className="space-y-2">
              <Label>PDR (gaz)</Label>
              <Input value={formData.pdr}
                onChange={(e) => handleChange("pdr", e.target.value)}
                disabled={!isEdit || !canWrite("contract.pdr")}
                data-testid="pdr" />
            </div>
          )}
          {!isHidden("contract.clc") && (
            <div className="space-y-2">
              <Label>CLC</Label>
              <Input value={formData.clc}
                onChange={(e) => handleChange("clc", e.target.value)}
                disabled={!isEdit || !canWrite("contract.clc")}
                data-testid="clc" />
            </div>
          )}
          {!isHidden("contract.numar_loc_consum") && (
            <div className="space-y-2">
              <Label>Nr. loc consum</Label>
              <Input value={formData.numar_loc_consum}
                onChange={(e) => handleChange("numar_loc_consum", e.target.value)}
                disabled={!isEdit || !canWrite("contract.numar_loc_consum")}
                data-testid="numar_loc_consum" />
            </div>
          )}
          {!isHidden("contract.tip_loc_consum") && (
            <div className="space-y-2">
              <Label>Tip loc consum</Label>
              <Select value={formData.tip_loc_consum}
                onValueChange={(v) => handleChange("tip_loc_consum", v)}
                disabled={!isEdit || !canWrite("contract.tip_loc_consum")}>
                <SelectTrigger data-testid="tip_loc_consum"><SelectValue placeholder="Selectează..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="rezidential">Rezidențial</SelectItem>
                  <SelectItem value="comercial">Comercial</SelectItem>
                  <SelectItem value="industrial">Industrial</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {!isHidden("contract.consumption_county") && (
            <div className="space-y-2">
              <Label>Județ loc consum</Label>
              <Input value={formData.consumption_county}
                onChange={(e) => handleChange("consumption_county", e.target.value)}
                disabled={!isEdit || !canWrite("contract.consumption_county")}
                data-testid="consumption_county" />
            </div>
          )}
          {!isHidden("contract.consumption_locality") && (
            <div className="space-y-2">
              <Label>Localitate loc consum</Label>
              <Input value={formData.consumption_locality}
                onChange={(e) => handleChange("consumption_locality", e.target.value)}
                disabled={!isEdit || !canWrite("contract.consumption_locality")}
                data-testid="consumption_locality" />
            </div>
          )}
          {!isHidden("contract.consumption_street") && (
            <div className="space-y-2 md:col-span-2">
              <Label>Stradă loc consum</Label>
              <Input value={formData.consumption_street}
                onChange={(e) => handleChange("consumption_street", e.target.value)}
                disabled={!isEdit || !canWrite("contract.consumption_street")}
                data-testid="consumption_street" />
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
